import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Z_INDEX_MODAL_NESTED } from '@/components/ui/zIndex';
import { useMatter } from '@/hooks/useMatter';
import { useProviderServicesRequestTemplateGetQuery } from '@/services/api/generated/providerDestinationsService';
import { useGetMatterQuery } from '@/services/api/mattersService';
import { useGetProviderDirectoryServiceQuery } from '@/services/api/providerDirectoryV2Service';
import { useGetMedicalProviderQuery } from '@/services/api/treatmentService';
import type { MedicalProvider } from '@/services/types/treatment-types';
import { ProviderIdentityLifecycleStatus } from '@/services/types/treatment-types';
import {
  DestinationScope,
  RequestTemplateIntent,
} from '@/types/provider-destinations';

import { DestinationSelectionStep } from './DestinationSelectionStep';
import { IdentityCheckStep } from './IdentityCheckStep';
import { IdentityRelinkStep } from './IdentityRelinkStep';
import { ReviewStep } from './ReviewStep';
import { ScopeSelectionStep } from './ScopeSelectionStep';
import { getThreadedRequestCount } from './threading';
import type { WizardStep } from './useCreateRequestWizard';
import { useCreateRequestWizard } from './useCreateRequestWizard';
import { useWizardSubmit } from './useWizardSubmit';

// ---------------------------------------------------------------------------
// Props (backward-compatible with old CreateProviderRequestDialog)
// ---------------------------------------------------------------------------

interface CreateRequestWizardDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  provider: MedicalProvider;
}

// ---------------------------------------------------------------------------
// Step metadata
// ---------------------------------------------------------------------------

const STEP_TITLE: Record<WizardStep, string> = {
  identity_check: 'Verify Provider Identity',
  identity_relink: 'Re-link Provider Service',
  scopes: 'Select Scopes',
  destinations: 'Choose Destinations',
  review: 'Review & Send',
};

const TEMPLATE_DEFAULT_SCOPES: Record<
  RequestTemplateIntent,
  DestinationScope[]
> = {
  [RequestTemplateIntent.STANDARD]: [
    DestinationScope.RECORDS,
    DestinationScope.BILLS,
  ],
  [RequestTemplateIntent.HOSPITAL]: [
    DestinationScope.RECORDS,
    DestinationScope.BILLS,
    DestinationScope.RADIOLOGY,
    DestinationScope.ER_PHYSICIAN,
  ],
};

const normalizeTemplateIntent = (intent: unknown): RequestTemplateIntent => {
  if (intent === RequestTemplateIntent.HOSPITAL) {
    return RequestTemplateIntent.HOSPITAL;
  }
  return RequestTemplateIntent.STANDARD;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CreateRequestWizardDialog = ({
  isOpen,
  setIsOpen,
  provider: initialProvider,
}: CreateRequestWizardDialogProps) => {
  const { matterId } = useMatter();
  const { data: matterData } = useGetMatterQuery(matterId);
  const [searchScope, setSearchScope] = useState<DestinationScope | null>(null);
  const [didHydrateTemplateDefaults, setDidHydrateTemplateDefaults] =
    useState(false);
  const [didHydrateDateOfLoss, setDidHydrateDateOfLoss] = useState(false);

  // Re-fetch the provider so we get fresh data after relink mutations
  const { data: freshProvider } = useGetMedicalProviderQuery(
    { matterId, providerId: initialProvider.id },
    { skip: !isOpen },
  );
  const provider = freshProvider ?? initialProvider;

  const primaryProviderServiceId = provider.provider_service_ref_id ?? null;

  // Determine whether identity check step is needed (live value)
  const needsIdentityCheck = Boolean(
    provider.identity_status &&
      primaryProviderServiceId &&
      provider.identity_status !== ProviderIdentityLifecycleStatus.RESOLVED,
  );

  // Capture the initial value when the dialog opens so that async cache
  // invalidation (e.g. after resolveIdentity) does not reset the wizard step.
  const [initialNeedsIdentityCheck, setInitialNeedsIdentityCheck] =
    useState(false);

  // Guard: prevents the step-initialization effect from re-running after the
  // initial step has been set (e.g. when freshProvider loads and
  // needsIdentityCheck changes).
  const didSetInitialStep = useRef(false);

  // Build the ordered step list dynamically using the captured initial value
  const stepSequence: WizardStep[] = useMemo(() => {
    const steps: WizardStep[] = [];
    if (initialNeedsIdentityCheck) {
      steps.push('identity_check');
    }
    steps.push('scopes', 'destinations', 'review');
    return steps;
  }, [initialNeedsIdentityCheck]);

  const totalSteps = stepSequence.filter(
    (s) => s !== 'identity_relink',
  ).length;

  const getStepNumber = (step: WizardStep): number => {
    if (step === 'identity_relink') {
      // Show same step number as identity_check
      const idx = stepSequence.indexOf('identity_check');
      return idx >= 0 ? idx + 1 : 1;
    }
    const idx = stepSequence.indexOf(step);
    return idx >= 0 ? idx + 1 : 1;
  };

  const { data: requestTemplate } = useProviderServicesRequestTemplateGetQuery(
    { providerServiceId: primaryProviderServiceId ?? '' },
    { skip: !primaryProviderServiceId || !isOpen },
  );

  const billingProviderServiceId = useMemo(() => {
    if (!requestTemplate) return null;
    if (requestTemplate.billing_mode !== 'DIFFERENT_BILLING_ORG') return null;
    return requestTemplate.billing_service_ref_id
      ? String(requestTemplate.billing_service_ref_id)
      : null;
  }, [requestTemplate]);

  const getProviderServiceIdForScope = useCallback(
    (scope: DestinationScope): string | null => {
      if (scope === DestinationScope.BILLS && billingProviderServiceId) {
        return billingProviderServiceId;
      }
      return primaryProviderServiceId;
    },
    [billingProviderServiceId, primaryProviderServiceId],
  );

  const { data: billingProviderService } = useGetProviderDirectoryServiceQuery(
    { serviceId: billingProviderServiceId ?? '' },
    {
      skip:
        !billingProviderServiceId ||
        billingProviderServiceId === primaryProviderServiceId,
    },
  );

  const billingOrganizationName = billingProviderService?.name ?? null;

  const {
    state,
    selectedScopes,
    canProceedToDestinations,
    canProceedToReview,
    canSubmit,
    setStep,
    toggleScope,
    setDestination,
    setCreateWithoutDestination,
    setSaveBehavior,
    setScopeFollowUpPhone,
    hydrateScopeFollowUpPhone,
    setNotes,
    setServiceStartDate,
    setServiceEndDate,
    initializeFromTemplate,
    setSubmitting,
    reset,
  } = useCreateRequestWizard();

  // When dialog opens, capture the initial identity check requirement and set
  // the first step.  The ref guard ensures this runs exactly once per dialog
  // open so that async cache updates (freshProvider arriving, RTK invalidation
  // after resolveIdentity) do not reset the wizard step mid-flow.
  useEffect(() => {
    if (isOpen && !didSetInitialStep.current) {
      didSetInitialStep.current = true;
      setInitialNeedsIdentityCheck(needsIdentityCheck);
      setStep(needsIdentityCheck ? 'identity_check' : 'scopes');
    }
  }, [isOpen, needsIdentityCheck, setStep]);

  // Reset wizard when dialog closes
  useEffect(() => {
    if (!isOpen) {
      didSetInitialStep.current = false;
      setInitialNeedsIdentityCheck(false);
      reset();
      setSearchScope(null);
      setDidHydrateTemplateDefaults(false);
      setDidHydrateDateOfLoss(false);
    }
  }, [isOpen, reset]);

  // Auto-populate service start date with the matter's date of loss (once)
  useEffect(() => {
    if (isOpen && matterData?.date_of_loss && !didHydrateDateOfLoss) {
      setServiceStartDate(matterData.date_of_loss);
      setDidHydrateDateOfLoss(true);
    }
  }, [isOpen, matterData?.date_of_loss, didHydrateDateOfLoss, setServiceStartDate]);

  useEffect(() => {
    if (!isOpen || !primaryProviderServiceId || !requestTemplate) return;
    if (didHydrateTemplateDefaults) return;

    const normalizedIntent = normalizeTemplateIntent(
      requestTemplate.template_intent,
    );
    const defaultScopesForIntent =
      TEMPLATE_DEFAULT_SCOPES[normalizedIntent] ??
      TEMPLATE_DEFAULT_SCOPES[RequestTemplateIntent.STANDARD];

    initializeFromTemplate(normalizedIntent, defaultScopesForIntent);
    setDidHydrateTemplateDefaults(true);
  }, [
    didHydrateTemplateDefaults,
    initializeFromTemplate,
    isOpen,
    primaryProviderServiceId,
    requestTemplate,
  ]);

  const { submit } = useWizardSubmit({
    selectedScopes,
    getProviderServiceIdForScope,
    matterId,
    providerId: provider.id,
    notes: state.notes,
    serviceStartDate: state.serviceStartDate,
    serviceEndDate: state.serviceEndDate,
    onSuccess: () => setIsOpen(false),
    setSubmitting,
  });

  const handleTemplateTypeChange = (templateType: RequestTemplateIntent) => {
    const defaultScopesForTemplate =
      TEMPLATE_DEFAULT_SCOPES[templateType] ??
      TEMPLATE_DEFAULT_SCOPES[RequestTemplateIntent.STANDARD];
    initializeFromTemplate(templateType, defaultScopesForTemplate);
  };

  // Navigation handlers
  const handleBack = () => {
    if (state.step === 'identity_relink') {
      setStep('identity_check');
    } else if (state.step === 'scopes' && initialNeedsIdentityCheck) {
      setStep('identity_check');
    } else if (state.step === 'destinations') {
      if (searchScope) {
        setSearchScope(null);
      } else {
        setStep('scopes');
      }
    } else if (state.step === 'review') {
      setStep('destinations');
    }
  };

  const handleNext = () => {
    if (state.step === 'scopes' && canProceedToDestinations) {
      setStep('destinations');
    } else if (state.step === 'destinations' && canProceedToReview) {
      setStep('review');
    } else if (state.step === 'review' && canSubmit) {
      submit();
    }
  };

  // Identity check handlers
  const handleIdentityAccepted = () => {
    setStep('scopes');
  };

  const handleIdentityRejected = () => {
    setStep('identity_relink');
  };

  const handleRelinked = () => {
    // After relink, go back to identity check with refreshed data
    setStep('identity_check');
  };

  // Button labels
  const getNextLabel = () => {
    if (state.step === 'review') {
      if (state.isSubmitting) return 'Sending...';
      const count = getThreadedRequestCount(selectedScopes);
      return count > 1 ? `Create ${count} Requests` : 'Create Request';
    }
    return 'Next';
  };

  const isNextDisabled = () => {
    if (state.step === 'identity_check') return true; // Use dedicated buttons
    if (state.step === 'identity_relink') return true; // Use dedicated buttons
    if (state.step === 'scopes') return !canProceedToDestinations;
    if (state.step === 'destinations') return !canProceedToReview;
    if (state.step === 'review') return !canSubmit;
    return false;
  };

  // Show back button logic
  const showBackButton =
    state.step !== 'identity_check' &&
    state.step !== 'identity_relink' &&
    !(state.step === 'scopes' && !initialNeedsIdentityCheck);

  // Provider city extracted from address
  const providerCity =
    provider.address
      ?.split(',')
      .map((s) => s.trim())
      .find((_, i, arr) => i === arr.length - 2) ?? undefined;

  return (
    <Dialog
      open={isOpen}
      onClose={setIsOpen}
      size="4xl"
      zIndex={Z_INDEX_MODAL_NESTED}
      nested
    >
      <DialogTitle>
        <div className="flex items-center justify-between">
          <span>
            Create Record Request:{' '}
            <span className="font-normal">{provider.name}</span>
          </span>
          <span className="text-sm font-normal text-zinc-400">
            Step {getStepNumber(state.step)} of {totalSteps} &mdash;{' '}
            {STEP_TITLE[state.step]}
          </span>
        </div>
      </DialogTitle>

      <DialogBody>
        {state.step === 'identity_check' && (
          <IdentityCheckStep
            provider={provider}
            matterId={matterId}
            onAccept={handleIdentityAccepted}
            onReject={handleIdentityRejected}
          />
        )}

        {state.step === 'identity_relink' && (
          <IdentityRelinkStep
            provider={provider}
            matterId={matterId}
            onRelinked={handleRelinked}
            onBack={() => setStep('identity_check')}
          />
        )}

        {state.step === 'scopes' && (
          <ScopeSelectionStep
            scopeSelections={state.scopeSelections}
            templateType={state.templateType}
            providerServiceId={primaryProviderServiceId}
            onToggleScope={toggleScope}
            onSetTemplateType={handleTemplateTypeChange}
            serviceStartDate={state.serviceStartDate}
            serviceEndDate={state.serviceEndDate}
            onSetServiceStartDate={setServiceStartDate}
            onSetServiceEndDate={setServiceEndDate}
          />
        )}

        {state.step === 'destinations' && (
          <DestinationSelectionStep
            selectedScopes={selectedScopes}
            primaryProviderServiceId={primaryProviderServiceId}
            billingProviderServiceId={billingProviderServiceId}
            billingOrganizationName={billingOrganizationName}
            providerName={provider.name ?? ''}
            providerCity={providerCity}
            searchScope={searchScope}
            onSetSearchScope={setSearchScope}
            onSetDestination={setDestination}
            onSetCreateWithoutDestination={setCreateWithoutDestination}
            onSetSaveBehavior={setSaveBehavior}
            onSetScopeFollowUpPhone={setScopeFollowUpPhone}
            onHydrateScopeFollowUpPhone={hydrateScopeFollowUpPhone}
            hydratedFollowUpPhoneScopes={
              state.hydratedFollowUpPhoneScopes
            }
          />
        )}

        {state.step === 'review' && (
          <ReviewStep
            selectedScopes={selectedScopes}
            notes={state.notes}
            onSetNotes={setNotes}
          />
        )}
      </DialogBody>

      <DialogActions>
        {showBackButton ? (
          <Button
            onClick={handleBack}
            color="white"
            disabled={state.isSubmitting}
          >
            Back
          </Button>
        ) : (
          <Button onClick={() => setIsOpen(false)} color="white">
            Cancel
          </Button>
        )}
        {state.step !== 'identity_check' &&
          state.step !== 'identity_relink' && (
            <Button
              onClick={handleNext}
              color="indigo-steel"
              disabled={isNextDisabled()}
            >
              {getNextLabel()}
            </Button>
          )}
        {state.step === 'identity_check' && (
          <Button onClick={() => setStep('scopes')} color="white">
            Skip
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Backward-compatible alias
export const CreateProviderRequestDialog = CreateRequestWizardDialog;
